import React, { useMemo, useState } from "react";
import { SectionTitle, InputCss } from "../../_parts.js";
import BorrowerSteppedForm from "../shared/BorrowerSteppedForm.jsx";
import FormField, { FormFieldRow } from "../shared/FormField.jsx";
import FormUploadsPanel from "../shared/FormUploadsPanel.jsx";
import FormReviewPanel from "../shared/FormReviewPanel.jsx";
import { getDraftStorageKey, loadFormDraft } from "../shared/useBorrowerFormDraft.js";
import { useBtlUploads } from "../shared/useBtlUploads.js";
import {
  buildUploadLabelMap,
  isNonNegativeNumber,
  isValidEmail,
} from "../shared/formValidation.js";
import PhoneField from "../../../../components/ui/PhoneField.jsx";
import { arePhonesValid, isValidPhone, PHONE_ERROR_MOBILE } from "../../../../utils/validatePhone.js";

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const MIN_PARTNERS = 2;
const MAX_PARTNERS = 4;

export default function PartnershipForm({
  onBack,
  onNext,
  initialPayload,
  mode = "wizard",
  productId,
  isSubmitting = false,
}) {
  const savedDraft = loadFormDraft(getDraftStorageKey("partnership", productId || initialPayload?.productId));
  const initial = savedDraft?.btl?.partnership || initialPayload?.btl?.partnership || null;

  const [partners, setPartners] = useState(
    initial?.partners?.length
      ? initial.partners
      : [
          { id: uid(), firstName: "", surname: "", phone: "", email: "", credit: "", propertyAddress: "", props: "" },
          { id: uid(), firstName: "", surname: "", phone: "", email: "", credit: "", propertyAddress: "", props: "" },
        ]
  );

  const { uploads, uploadingFiles, handleUpload, handleRemove } = useBtlUploads(initial?.uploads || {});
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  const canAddMore = partners.length < MAX_PARTNERS;
  const lead = partners[0] || {};

  const update = (id, key, val) =>
    setPartners((arr) => arr.map((p) => (p.id === id ? { ...p, [key]: val } : p)));

  const addApplicant = () => {
    if (partners.length >= MAX_PARTNERS) return;
    setPartners((p) => [
      ...p,
      { id: uid(), firstName: "", surname: "", phone: "", email: "", credit: "", propertyAddress: "", props: "" },
    ]);
  };

  const removeApplicant = (id) => {
    if (partners.length <= MIN_PARTNERS) return;
    setPartners((list) => list.filter((p) => p.id !== id));
  };

  const touch = (key) => setTouched((t) => ({ ...t, [key]: true }));

  const payloadObj = useMemo(
    () => ({
      ...(initialPayload || {}),
      btl: {
        ...(initialPayload?.btl || {}),
        borrowerType: "Partnership",
        partnership: {
          partners,
          uploads,
          propertyAddress: lead.propertyAddress || "",
          props: lead.props || "",
        },
      },
      formData: {
        ...(initialPayload?.formData || {}),
        propertyAddress: lead.propertyAddress || "",
        property_address: lead.propertyAddress || "",
        props: lead.props || "",
      },
    }),
    [initialPayload, partners, uploads, lead.propertyAddress, lead.props]
  );

  const draftKey = getDraftStorageKey("partnership", productId || initialPayload?.productId);

  const validateDetails = () => {
    const err = {};
    if (partners.length < MIN_PARTNERS) err._partners = `At least ${MIN_PARTNERS} partners are required.`;
    if (!lead.firstName?.trim()) err.lead_firstName = "First Name is required.";
    if (!lead.surname?.trim()) err.lead_surname = "Surname is required.";
    if (!isValidPhone(lead.phone)) err.lead_phone = PHONE_ERROR_MOBILE;
    if (!lead.email?.trim()) err.lead_email = "Email is required.";
    else if (!isValidEmail(lead.email)) err.lead_email = "Enter a valid email address.";
    setFieldErrors((e) => ({ ...e, ...err }));
    setTouched((t) => ({
      ...t,
      lead_firstName: true,
      lead_surname: true,
      lead_phone: true,
      lead_email: true,
      _partners: true,
    }));
    return !Object.keys(err).length;
  };

  const validateProperty = () => {
    const err = {};
    if (!lead.credit) err.lead_credit = "Please select credit history.";
    if (!lead.propertyAddress?.trim()) err.lead_propertyAddress = "Security Address is required.";
    if (!isNonNegativeNumber(lead.props)) err.lead_props = "Enter a valid number (0 or more).";
    setFieldErrors((e) => ({ ...e, ...err }));
    setTouched((t) => ({
      ...t,
      lead_credit: true,
      lead_propertyAddress: true,
      lead_props: true,
    }));
    return !Object.keys(err).length;
  };

  const stepErrors = useMemo(
    () => ({
      details: Boolean(
        fieldErrors.lead_firstName ||
          fieldErrors.lead_surname ||
          fieldErrors.lead_phone ||
          fieldErrors.lead_email ||
          fieldErrors._partners
      ),
      property: Boolean(
        fieldErrors.lead_credit || fieldErrors.lead_propertyAddress || fieldErrors.lead_props
      ),
      documents: false,
      review: false,
    }),
    [fieldErrors]
  );

  const validateStep = async (stepIndex) => {
    if (stepIndex === 0) return validateDetails();
    if (stepIndex === 1) return validateProperty();
    return true;
  };

  const finish = (extras = {}) => {
    onNext?.({
      type: "partnership",
      partners,
      uploads,
      propertyAddress: lead.propertyAddress,
      props: lead.props,
      payloadObj,
      submitFromReview: true,
      agree1: extras.agree1,
      agree2: extras.agree2,
    });
  };

  const uploadLabels = useMemo(() => buildUploadLabelMap(), []);

  return (
    <>
      <BorrowerSteppedForm
        title="PARTNERSHIP REGISTRATION FORM"
        subtitle="Please complete all required fields and upload necessary documents"
        draftKey={draftKey}
        draftSnapshot={payloadObj}
        stepErrors={stepErrors}
        validateStep={validateStep}
        onOuterBack={onBack}
        mode={mode}
        detailsContinueDisabled={!arePhonesValid(partners.map((p, i) => ({ value: p.phone, required: i === 0 })))}
        renderDetails={() => (
          <div className="space-y-4">
            <SectionTitle>Personal Information</SectionTitle>
            {fieldErrors._partners && touched._partners ? (
              <p className="text-xs text-rose-600" role="alert">
                {fieldErrors._partners}
              </p>
            ) : null}

            {partners.map((p, idx) => (
              <div key={p.id} className="rounded-xl border border-slate-200 p-3 md:p-4">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-sm font-bold text-slate-900">
                    {idx === 0 ? "Main Partner" : `Partner ${idx + 1}`}
                  </p>
                  {partners.length > MIN_PARTNERS ? (
                    <button
                      type="button"
                      onClick={() => removeApplicant(p.id)}
                      className="text-xs font-medium text-red-500 hover:text-red-600"
                      aria-label={`Remove partner ${idx + 1}`}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
                <FormFieldRow>
                  <FormField
                    id={`partner-${p.id}-firstName`}
                    label="First Name"
                    required={idx === 0}
                    error={idx === 0 ? fieldErrors.lead_firstName : undefined}
                    touched={idx === 0 ? touched.lead_firstName : false}
                  >
                    <input
                      id={`partner-${p.id}-firstName`}
                      className={`input w-full ${idx === 0 && fieldErrors.lead_firstName ? "border-rose-300" : ""}`}
                      value={p.firstName}
                      onChange={(e) => update(p.id, "firstName", e.target.value)}
                      onBlur={() => idx === 0 && touch("lead_firstName")}
                    />
                  </FormField>
                  <FormField
                    id={`partner-${p.id}-surname`}
                    label="Surname"
                    required={idx === 0}
                    error={idx === 0 ? fieldErrors.lead_surname : undefined}
                    touched={idx === 0 ? touched.lead_surname : false}
                  >
                    <input
                      id={`partner-${p.id}-surname`}
                      className="input w-full"
                      value={p.surname}
                      onChange={(e) => update(p.id, "surname", e.target.value)}
                      onBlur={() => idx === 0 && touch("lead_surname")}
                    />
                  </FormField>
                  <FormField id={`partner-${p.id}-phone`} label="Phone Number" required={idx === 0}>
                    <PhoneField
                      id={`partner-${p.id}-phone`}
                      value={p.phone}
                      required={idx === 0}
                      onChange={(next) => update(p.id, "phone", next)}
                      onBlur={() => idx === 0 && touch("lead_phone")}
                    />
                  </FormField>
                  <FormField
                    id={`partner-${p.id}-email`}
                    label="Email"
                    required={idx === 0}
                    error={idx === 0 ? fieldErrors.lead_email : undefined}
                    touched={idx === 0 ? touched.lead_email : false}
                  >
                    <input
                      id={`partner-${p.id}-email`}
                      type="email"
                      inputMode="email"
                      className="input w-full"
                      value={p.email}
                      onChange={(e) => update(p.id, "email", e.target.value)}
                      onBlur={() => idx === 0 && touch("lead_email")}
                    />
                  </FormField>
                </FormFieldRow>
              </div>
            ))}

            <div className="text-center">
              <button
                type="button"
                onClick={addApplicant}
                disabled={!canAddMore}
                className={`px-4 py-2 rounded-xl text-white text-sm ${
                  canAddMore ? "bg-emerald-500 hover:bg-emerald-600" : "bg-emerald-300 cursor-not-allowed"
                }`}
              >
                + Add Applicant
              </button>
            </div>
          </div>
        )}
        renderProperty={() => (
          <div>
            <SectionTitle>Property &amp; finance</SectionTitle>
            <FormField
              id="lead-credit"
              label="Credit History"
              required
              error={fieldErrors.lead_credit}
              touched={touched.lead_credit}
            >
              <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Credit History">
                {["Good", "Bad", "Poor"].map((opt) => (
                  <label
                    key={opt}
                    className={`px-4 py-2 rounded-full border cursor-pointer ${
                      lead.credit === opt
                        ? "bg-sky-400 text-white border-sky-400"
                        : "bg-white text-slate-700 border-slate-200"
                    }`}
                  >
                    <input
                      type="radio"
                      name="lead_credit"
                      className="hidden"
                      checked={lead.credit === opt}
                      onChange={() => update(lead.id, "credit", opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </FormField>
            <FormFieldRow className="mt-4">
              <FormField
                id="lead-props"
                label="Number of properties owned"
                required
                error={fieldErrors.lead_props}
                touched={touched.lead_props}
              >
                <input
                  id="lead-props"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  className="input w-full"
                  value={lead.props || ""}
                  onChange={(e) => update(lead.id, "props", e.target.value)}
                  onBlur={() => touch("lead_props")}
                />
              </FormField>
              <FormField
                id="lead-propertyAddress"
                label="Security Address"
                required
                error={fieldErrors.lead_propertyAddress}
                touched={touched.lead_propertyAddress}
                className="sm:col-span-2"
              >
                <input
                  id="lead-propertyAddress"
                  className="input w-full"
                  value={lead.propertyAddress || ""}
                  onChange={(e) => update(lead.id, "propertyAddress", e.target.value)}
                  onBlur={() => touch("lead_propertyAddress")}
                />
              </FormField>
            </FormFieldRow>
          </div>
        )}
        renderDocuments={() => (
          <FormUploadsPanel
            uploads={uploads}
            uploadingFiles={uploadingFiles}
            onUpload={handleUpload}
            onRemove={handleRemove}
          />
        )}
        renderReview={({ goToStep, onFooterStateChange }) => (
          <FormReviewPanel
            hideSubmitButton
            onFooterStateChange={onFooterStateChange}
            sections={[
              {
                stepId: "details",
                stepIndex: 0,
                title: "Your details",
                content: partners.map((p, i) => (
                  <div key={p.id} className="border-b border-slate-100 pb-2 mb-2 last:border-0">
                    <p className="font-semibold text-slate-800">{i === 0 ? "Main Partner" : `Partner ${i + 1}`}</p>
                    <p className="text-sm">
                      {p.firstName} {p.surname} · {p.phone} · {p.email}
                    </p>
                  </div>
                )),
              },
              {
                stepId: "property",
                stepIndex: 1,
                title: "Property & finance",
                rows: [
                  { label: "Credit History", value: lead.credit },
                  { label: "Properties owned", value: lead.props },
                  { label: "Security Address", value: lead.propertyAddress },
                ],
              },
            ]}
            uploads={uploads}
            uploadLabels={uploadLabels}
            onEditStep={goToStep}
            isSubmitting={isSubmitting}
            onSubmit={({ agree1, agree2 }) => finish({ agree1, agree2 })}
          />
        )}
      />
      <InputCss />
    </>
  );
}
